import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShareService, shareKeys } from '../../../services/shareService';

/**
 * Content type constant for risk calculator shares
 */
export const RISK_CALCULATOR_CONTENT_TYPE = 'risk_calculator';

/**
 * Hook to create a risk calculator share
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object
 */
export function useCreateRiskCalculatorShare(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ inputData, resultsData, enrichedData, title = '' }) => {
      return ShareService.createShare({
        contentType: RISK_CALCULATOR_CONTENT_TYPE,
        data: {
          input_data: inputData,
          results_data: resultsData,
          enriched_data: enrichedData,
        },
        title,
      });
    },
    onSuccess: () => {
      // Invalidate the shares list cache
      queryClient.invalidateQueries({ queryKey: shareKeys.lists() });
    },
    ...options,
  });
}

/**
 * Hook to fetch a share by token
 * @param {string} token - Share token
 * @param {Object} options - React Query options
 * @returns {Object} Query result
 */
export function useShare(token, options = {}) {
  return useQuery({
    queryKey: shareKeys.detail(token),
    queryFn: () => ShareService.getShare(token),
    enabled: !!token,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 410 errors
      if (error?.response?.status === 404 || error?.response?.status === 410) {
        return false;
      }
      return failureCount < 3;
    },
    ...options,
  });
}

/**
 * Hook to fetch user's shares list (optionally filtered by content type)
 * @param {string} [contentType] - Filter by content type
 * @param {Object} options - React Query options
 * @returns {Object} Query result
 */
export function useSharesList(contentType = null, options = {}) {
  return useQuery({
    queryKey: shareKeys.list(contentType),
    queryFn: () => ShareService.listShares(contentType),
    ...options,
  });
}

/**
 * Hook to fetch user's risk calculator shares
 * @param {Object} options - React Query options
 * @returns {Object} Query result
 */
export function useRiskCalculatorSharesList(options = {}) {
  return useSharesList(RISK_CALCULATOR_CONTENT_TYPE, options);
}

/**
 * Hook to delete a share
 * @param {Object} options - React Query mutation options
 * @returns {Object} Mutation object
 */
export function useDeleteShare(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token) => ShareService.deleteShare(token),
    onSuccess: (_, token) => {
      // Invalidate the shares list cache
      queryClient.invalidateQueries({ queryKey: shareKeys.lists() });
      // Remove the deleted share from cache
      queryClient.removeQueries({ queryKey: shareKeys.detail(token) });
    },
    ...options,
  });
}
